'use client';

/**
 * System Status Banner
 *
 * Displays active system incidents to all users.
 * Polls the incidents API and shows AI-generated explanations.
 */

import { useEffect, useState, useRef } from 'react';
import { captureClientError } from '@/lib/error-reporting/client';
import { AlertTriangle, CheckCircle, Loader2, X, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  getServiceDisplayName,
  getStatusColor,
  type IncidentStatus,
  type AffectedService,
} from '@/lib/utils/incident-detection';

interface Incident {
  id: string;
  error_signature: string;
  status: IncidentStatus;
  affected_service: AffectedService;
  ai_explanation: string | null;
  detected_at: string;
}

interface SystemStatusBannerProps {
  className?: string;
  /** How often to poll for updates (ms). Default: 120000 (120s) */
  pollInterval?: number;
}

/** Transient network errors that should not be reported to Sentry */
function isNetworkError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  const message = error instanceof Error ? error.message : String(error);
  return /Failed to fetch|Load failed|NetworkError|network/i.test(message);
}

export function SystemStatusBanner({ className, pollInterval = 120000 }: SystemStatusBannerProps) {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [networkError, setNetworkError] = useState(false);
  const loadedRef = useRef(false);
  // Require 2 consecutive failures before showing the fallback — avoids false positives from transient blips
  const consecutiveFailsRef = useRef(0);
  // Holds the scheduled next-poll timeout so it can be cancelled on cleanup or visibility change
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const safeFetch = async () => {
      // Skip fetch when tab is hidden or browser is offline
      if (document.visibilityState === 'hidden') return;
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;

      try {
        const res = await fetch('/api/system/incidents', {
          cache: 'no-store',
          signal: controller.signal,
        });
        if (res.ok) {
          const data = await res.json();
          setIncidents(data.incidents || []);
          // Reset failure tracking on a successful fetch
          consecutiveFailsRef.current = 0;
          setNetworkError(false);
        }
      } catch (error) {
        if (isNetworkError(error)) {
          // Track consecutive failures — show fallback only after 2 in a row to avoid flicker
          consecutiveFailsRef.current += 1;
          if (consecutiveFailsRef.current >= 2) {
            setNetworkError(true);
          }
        } else {
          captureClientError(error, 'client_runtime', {
            component: 'SystemStatusBanner',
            action: 'fetch_incidents',
          }).catch(() => {});
        }
      } finally {
        if (!loadedRef.current) {
          loadedRef.current = true;
          setIsLoading(false);
        }
      }
    };

    // Recursive setTimeout instead of setInterval so the interval can grow dynamically.
    // On each consecutive failure the wait doubles (120s → 240s → 480s), capped at 600s.
    // A successful fetch resets consecutiveFailsRef back to 0, returning to the base interval.
    const scheduleNext = () => {
      const backoffMultiplier = Math.min(Math.pow(2, consecutiveFailsRef.current), 5);
      const nextInterval = pollInterval * backoffMultiplier;
      timeoutRef.current = setTimeout(() => {
        // safeFetch has its own try-catch; .catch() prevents unhandled-rejection if it ever throws.
        safeFetch()
          .catch(() => {})
          .then(scheduleNext);
      }, nextInterval);
    };

    safeFetch().then(scheduleNext);

    // Re-fetch immediately when tab becomes visible again and reschedule from that point
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        safeFetch().then(scheduleNext);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      controller.abort();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [pollInterval]);

  // Filter out dismissed incidents
  const activeIncidents = incidents.filter((i) => !dismissed.has(i.id));

  // Show nothing during initial load
  if (isLoading) return null;

  // Show local fallback when API is unreachable and no cached incidents are visible
  if (networkError && activeIncidents.length === 0) {
    return (
      <div className={cn('w-full lg:ml-56', className)}>
        <LocalFallbackBanner onDismiss={() => setNetworkError(false)} />
      </div>
    );
  }

  // Show nothing when there are no active incidents
  if (activeIncidents.length === 0) return null;

  const handleDismiss = (id: string) => {
    setDismissed((prev) => new Set([...prev, id]));
  };

  return (
    <div className={cn('w-full lg:ml-56', className)}>
      {activeIncidents.map((incident) => (
        <IncidentBanner
          key={incident.id}
          incident={incident}
          onDismiss={() => handleDismiss(incident.id)}
        />
      ))}
    </div>
  );
}

function LocalFallbackBanner({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div
      className={cn(
        'relative flex items-start gap-3 px-4 py-3 border-b',
        'bg-amber-500/10 border-amber-500/20 text-amber-900 dark:text-amber-200'
      )}
      role="alert"
      aria-live="polite"
    >
      <Loader2 className="h-5 w-5 flex-shrink-0 mt-0.5 animate-spin text-amber-600 dark:text-amber-400" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium">Connectivity Issue</span>
          <span className="text-xs px-2 py-0.5 rounded-full capitalize bg-amber-500/20">
            detecting
          </span>
        </div>
        <p className="text-sm mt-1 opacity-90">
          We&apos;re having trouble connecting to our services. This usually resolves within a few
          minutes. If the issue persists, please try refreshing the page.
        </p>
      </div>
      <button
        onClick={onDismiss}
        className="flex-shrink-0 p-1 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
        aria-label="Dismiss notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

interface IncidentBannerProps {
  incident: Incident;
  onDismiss: () => void;
}

function IncidentBanner({ incident, onDismiss }: IncidentBannerProps) {
  const statusColors: Record<IncidentStatus, string> = {
    investigating: 'bg-amber-500/10 border-amber-500/20 text-amber-900 dark:text-amber-200',
    identified: 'bg-orange-500/10 border-orange-500/20 text-orange-900 dark:text-orange-200',
    monitoring: 'bg-blue-500/10 border-blue-500/20 text-blue-900 dark:text-blue-200',
    resolved: 'bg-green-500/10 border-green-500/20 text-green-900 dark:text-green-200',
  };

  const StatusIcon = {
    investigating: Loader2,
    identified: AlertTriangle,
    monitoring: CheckCircle,
    resolved: CheckCircle,
  }[incident.status];

  const serviceName = getServiceDisplayName(incident.affected_service);
  const explanation =
    incident.ai_explanation || "We're investigating an issue. Please try again in a few minutes.";

  return (
    <div
      className={cn(
        'relative flex items-start gap-3 px-4 py-3 border-b',
        statusColors[incident.status]
      )}
      role="alert"
      aria-live="polite"
    >
      <StatusIcon
        className={cn(
          'h-5 w-5 flex-shrink-0 mt-0.5',
          incident.status === 'investigating' && 'animate-spin',
          getStatusColor(incident.status)
        )}
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium">{serviceName} Issue</span>
          <span
            className={cn(
              'text-xs px-2 py-0.5 rounded-full capitalize',
              incident.status === 'investigating' && 'bg-amber-500/20',
              incident.status === 'identified' && 'bg-orange-500/20',
              incident.status === 'monitoring' && 'bg-blue-500/20'
            )}
          >
            {incident.status}
          </span>
        </div>

        <p className="text-sm mt-1 opacity-90">{explanation}</p>

        <div className="flex items-center gap-4 mt-2 text-xs opacity-70">
          <span>Detected {formatRelativeTime(incident.detected_at)}</span>
          <a
            href="https://status.supabase.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 hover:underline"
          >
            Status Page
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>

      <button
        onClick={onDismiss}
        className="flex-shrink-0 p-1 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
        aria-label="Dismiss notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/**
 * Formats a timestamp as relative time (e.g., "5 minutes ago")
 */
function formatRelativeTime(timestamp: string): string {
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;

  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  return new Date(timestamp).toLocaleDateString();
}

/**
 * Hook to report errors to the incident system
 */
export function useReportIncident() {
  const controllerRef = useRef<AbortController>(null);

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const reportError = async (error: unknown) => {
    try {
      controllerRef.current?.abort();
      controllerRef.current = new AbortController();

      const errorString =
        error instanceof Error ? `${error.name}: ${error.message}` : String(error);

      await fetch('/api/system/incidents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controllerRef.current.signal,
        body: JSON.stringify({
          error: errorString,
          url: typeof window !== 'undefined' ? window.location.href : undefined,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        }),
      });
    } catch (reportErr) {
      if (!isNetworkError(reportErr)) {
        captureClientError(reportErr, 'client_runtime', {
          component: 'useReportIncident',
          action: 'report_error_to_incident_system',
        }).catch(() => {});
      }
    }
  };

  return { reportError };
}
